import * as React from "react";
import { RouteComponentProps, withRouter } from "react-router";
import { UserContext } from "@socialcoach/services";

import { SocialNetworkProfiles, PageWrapper } from "@/components";
import { AppContext } from "@/providers";
import { CoachSettings, SettingsTags, PlayerAccount } from "@/interfaces";
import { useSocialCoachSession, usePlayerSettings, redirectPlayerIfSignUpNotFinished, URLS } from "@/utils";

import { PlayerMyCustomizations, VideoPageThemes } from "@/containers";
import { Tabs } from "@/components/ui/tabs";
import { PlayerProfileHeader } from "./components/PlayerProfileHeader";

import { useMemo } from "react";

interface SettingMenu {
  title: string;
  activeSection: SettingsTags;
}
interface OwnProps {
  successHandler: (user: UserContext) => void;
}

type Props = OwnProps & RouteComponentProps;

const IntegratedPlayerSettings: React.FC<Props> = ({ history, match: { params } }) => {
  const { userContext, refreshSession } = React.useContext(AppContext);
  const { playerId, session } = useSocialCoachSession();
  const { hasSetting } = usePlayerSettings(playerId);
  const hasMVPThemeAccess = useMemo(() => {
    return hasSetting(CoachSettings.PLAYER_MVP_ACCESS) && hasSetting(CoachSettings.PLAYER_MVP_THEMES);
  }, [hasSetting]);

  React.useEffect(() => {
    redirectPlayerIfSignUpNotFinished(userContext);
  }, [userContext]);

  const initialTab = (params["tab"] as SettingsTags) || "social-networks";

  const [activeSection, setActiveSection] = React.useState<SettingsTags>(initialTab);

  React.useEffect(() => {
    history.replace(URLS.player.settings.replace(":tab?", activeSection));
  }, [activeSection]);

  const playerAccount = useMemo<PlayerAccount | undefined>(() => {
    return {
      userId: session?.userId,
      playerId: session?.playerId,
      email: session?.email,
      firstName: session?.firstName,
      lastName: session?.lastName,
      phone: session?.phone,
      photoUrl: session?.photoUrl,
      title: session?.title,
      complianceDisclaimer: session?.complianceDisclaimer,
    };
  }, [session]);

  const menuList: SettingMenu[] = useMemo(() => {
    const list: SettingMenu[] = [
      {
        title: "Social Accounts",
        activeSection: "social-networks",
      },
      {
        title: "Customizations",
        activeSection: "customizations",
      },
    ];
    if (hasMVPThemeAccess) {
      list.push({
        title: "Video Page Themes",
        activeSection: "mvp",
      });
    }
    return list;
  }, [hasMVPThemeAccess]);

  const selectedContent = React.useMemo(() => {
    switch (activeSection) {
      case "social-networks":
        return <SocialNetworkProfiles />;
      case "customizations":
        return <PlayerMyCustomizations />;
      case "mvp":
        return <VideoPageThemes />;
      default:
        return null;
    }
  }, [activeSection]);

  return (
    <PageWrapper title="Settings" hideSeparator>
      <div className="flex flex-col gap-6">
        <PlayerProfileHeader playerAccount={playerAccount} fetching={!userContext} onSuccess={refreshSession} />

        <Tabs value={activeSection} onValueChange={val => setActiveSection(val as SettingsTags)}>
          <Tabs.List variant="menu" className="mb-2">
            {menuList.map(element => (
              <Tabs.Trigger key={element.activeSection} value={element.activeSection}>
                {element.title}
              </Tabs.Trigger>
            ))}
          </Tabs.List>
        </Tabs>

        <div className="w-full">{selectedContent}</div>
      </div>
    </PageWrapper>
  );
};

export const PlayerSettings = withRouter(IntegratedPlayerSettings);
